import { RouteConfig } from '@/routes';
import { BadgesState } from '@/types/updates-hub';
import { SidebarMode } from './sidebar.types';

// Службові ролі: пункт, доступний лише їм, показується тільки у власному режимі сайдбара.
const STAFF_ROLES = ['superadmin', 'admin', 'owner', 'developer', 'pm'];

export function checkItemVisibility(
  item: RouteConfig,
  mode: SidebarMode,
  userRoles: string[],
  isAuthenticated: boolean,
): boolean {
  if (item.isPublic) return true;
  if (!isAuthenticated) return false;

  const hasRoleAccess =
    !item.allowedRoles ||
    item.allowedRoles.length === 0 ||
    userRoles.some(role => item.allowedRoles!.map(r => r.toLowerCase()).includes(role));

  if (!hasRoleAccess) return false;

  const itemRoles = (item.allowedRoles || []).map(r => r.toLowerCase());

  switch (mode) {
    case 'admin':
      return itemRoles.includes('superadmin') || itemRoles.includes('admin');
    case 'developer':
      return itemRoles.includes('developer');
    case 'pm':
      return itemRoles.includes('pm');
    case 'owner':
      return itemRoles.includes('owner');
    case 'client':
    case 'expert':
    case 'general':
    default:
      return itemRoles.length === 0 || !itemRoles.every(r => STAFF_ROLES.includes(r));
  }
}

export function getBadgeForRoute(
  item: RouteConfig,
  badges: BadgesState,
  chatUnreadCount: number,
): number {
  if (item.name === 'Чат') return chatUnreadCount;
  if (item.path === '/expert-posts') return badges.expertPosts;
  if (item.name === 'Скринька' || item.path === '/notifications') return badges.notifications;
  if (item.path === '/finance' || item.name === 'Фінанси') return badges.payments;
  if (item.path === '/calendar' || item.name === 'Календар') return badges.calendar;
  if (item.path === '/questionnaires' || item.name === 'Опитування') return badges.questionnaires;
  if (item.path === '/experts' || item.name === 'Експерти') return badges.experts;
  if (item.path === '/expert/appointments' || item.path === '/appointments' || item.name === 'Зустрічі' || item.name === 'Мої записи') return badges.appointments;
  return 0;
}

export function isRouteSelected(item: RouteConfig, pathname: string): boolean {
  if (item.path === '/clients' && pathname.startsWith('/clients')) return true;
  if (item.path && pathname.startsWith(item.path) && item.path !== '/') return true;
  return item.path === pathname;
}

// Картка клієнта є і в режимі експерта (/client/:id), і в режимі продакта (/pm/client/:id).
const CLIENT_CARD_BASES: Partial<Record<SidebarMode, string>> = {
  expert: '/client',
  pm: '/pm/client',
};

// Розділи, що є в обох картках; решта при перемиканні режиму веде на дашборд клієнта.
const SHARED_CLIENT_CARD_TABS = ['dashboard', 'calendar'];

/** Клієнт, відкритий у картці поточного режиму, і шлях до нього в картці режиму nextMode (або null). */
export function resolveClientCardPath(
  pathname: string,
  currentMode: SidebarMode,
  nextMode: SidebarMode,
): { clientId: number; path: string } | null {
  const fromBase = CLIENT_CARD_BASES[currentMode];
  const toBase = CLIENT_CARD_BASES[nextMode];
  if (!fromBase || !toBase) return null;

  const match = pathname.match(new RegExp(`^${fromBase}/(\\d+)(?:/([^/]+))?`));
  if (!match) return null;

  const clientId = Number(match[1]);
  const tab = match[2] && SHARED_CLIENT_CARD_TABS.includes(match[2]) ? match[2] : 'dashboard';
  return { clientId, path: `${toBase}/${clientId}/${tab}` };
}
